// Real canvas-based image adjustment / enhancement pipeline (no mock data).

export interface Adjustments {
  exposure: number;     // -3..3
  contrast: number;     // -50..50
  shadows: number;      // -100..100
  highlights: number;   // -100..100
  vibrance: number;     // -100..100
  temperature: number;  // -100..100
  sharpen: number;      // 0..100
  denoise: number;      // 0..100
  upscale: number;      // target width in px, 0 = keep original
}

export const defaultAdjustments: Adjustments = {
  exposure: 0,
  contrast: 0,
  shadows: 0,
  highlights: 0,
  vibrance: 0,
  temperature: 0,
  sharpen: 0,
  denoise: 0,
  upscale: 0,
};

export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

export function buildCssFilter(a: Adjustments): string {
  const brightness = 100 + a.exposure * 12 + a.highlights * -0.15 + a.shadows * 0.2;
  const contrast = 100 + a.contrast;
  const saturate = 100 + a.vibrance;
  const hue = a.temperature * 0.5;
  return `brightness(${Math.max(10, brightness)}%) contrast(${Math.max(10, contrast)}%) saturate(${Math.max(0, saturate)}%) hue-rotate(${hue}deg)`;
}

function applyConvolution(ctx: CanvasRenderingContext2D, w: number, h: number, kernel: number[], divisor = 1) {
  const src = ctx.getImageData(0, 0, w, h);
  const dst = ctx.createImageData(w, h);
  const sd = src.data;
  const dd = dst.data;
  const side = Math.round(Math.sqrt(kernel.length));
  const half = Math.floor(side / 2);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0;
      for (let ky = 0; ky < side; ky++) {
        for (let kx = 0; kx < side; kx++) {
          const sx = Math.min(w - 1, Math.max(0, x + kx - half));
          const sy = Math.min(h - 1, Math.max(0, y + ky - half));
          const off = (sy * w + sx) * 4;
          const wt = kernel[ky * side + kx];
          r += sd[off] * wt;
          g += sd[off + 1] * wt;
          b += sd[off + 2] * wt;
        }
      }
      const o = (y * w + x) * 4;
      dd[o] = Math.min(255, Math.max(0, r / divisor));
      dd[o + 1] = Math.min(255, Math.max(0, g / divisor));
      dd[o + 2] = Math.min(255, Math.max(0, b / divisor));
      dd[o + 3] = sd[o + 3];
    }
  }
  ctx.putImageData(dst, 0, 0);
}

/** Draws + applies all adjustments onto the given canvas, mutating it in place. */
export function renderAdjustedImage(img: HTMLImageElement, canvas: HTMLCanvasElement, a: Adjustments) {
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  const maxUpscale = Math.min(a.upscale || 0, 3840);
  if (maxUpscale > w) {
    const scale = maxUpscale / w;
    w = Math.round(maxUpscale);
    h = Math.round(h * scale);
  }
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return ctx;
  ctx.filter = buildCssFilter(a);
  ctx.drawImage(img, 0, 0, w, h);
  ctx.filter = "none";

  // Denoise: light box-blur convolution, strength scaled by slider.
  if (a.denoise > 0) {
    const passes = a.denoise > 60 ? 2 : 1;
    for (let i = 0; i < passes; i++) {
      applyConvolution(ctx, w, h, [1, 1, 1, 1, 1, 1, 1, 1, 1], 9);
    }
  }

  // Sharpen: unsharp-mask style convolution kernel.
  if (a.sharpen > 0) {
    const amt = a.sharpen / 100;
    applyConvolution(ctx, w, h, [0, -amt, 0, -amt, 1 + 4 * amt, -amt, 0, -amt, 0], 1);
  }

  return ctx;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type = "image/jpeg", quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), type, quality);
  });
}

export function downloadCanvas(canvas: HTMLCanvasElement, filename: string, type = "image/jpeg", quality = 0.92) {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, type, quality);
}
